import {
	type ApiErrorResponse,
	type AuctionStatusEnum,
	type FanShopProductVariant,
	type FetchAuctionAuctionByAuctionIdData,
	type FetchAuctionAuctionsData,
	type FetchAuctionAuctionsMeData,
	type FetchAuctionAuctionsMeParams,
	type FetchAuctionAuctionsParams,
	type FrontAuctionBidDTO,
	minutesToMs,
	type PaginationWithSimplePaginationMode,
	type PostAuctionAuctionBidByAuctionIdData,
	secondsToMs
} from '@capsicum/common';
import {
	type InfiniteData,
	useInfiniteQuery,
	type UseInfiniteQueryOptions,
	useMutation,
	useQuery,
	type UseQueryOptions
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { rpAuctions } from '../api';

export interface FanShopProductVariantAuction extends FanShopProductVariant {
	auctionBids?: Array<FrontAuctionBidDTO>;
	auctionStatus?: AuctionStatusEnum;
}

type PostRPBidParams = {
	amount: string;
	auctionId: string;
	countryCode: string;
};

export const UsePlaceRPBidQueryKey = ['placeRPBid'] as const;

export type FanShopProductVariantExtended = any | FanShopProductVariant | FanShopProductVariantAuction;

export const UseRPAuctionsQueryKeyBuilder = (params?: FetchAuctionAuctionsParams, limit?: number) =>
	['fanshop-auctions', 'rpauctions', params, limit] as const;

export const UseRPAuctionDetailsQueryKeyBuilder = (auctionId?: string) =>
	['auctionProductUpdateFromWS', 'fanshop-auctions', 'rpauctions-details', auctionId] as const;

export const UseRPAuctionsMeQueryKeyBuilder = ['auctionProductUpdateFromWS', 'rpauctions-me'] as const;

export const useRPAuctionsQueries = (
	params?: FetchAuctionAuctionsParams,
	options?: Omit<
		UseInfiniteQueryOptions<FetchAuctionAuctionsData, Error, InfiniteData<FetchAuctionAuctionsData>>,
		'queryFn' | 'queryKey'
	>,
	limit = 10
) => {
	return useInfiniteQuery({
		...options,
		getNextPageParam: lastPage => {
			const metadata = lastPage.metadata as PaginationWithSimplePaginationMode['metadata'];

			const { current_page = 1, has_more_page } = metadata ?? {};

			return has_more_page ? current_page + 1 : undefined;
		},
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			return (await rpAuctions).fetchAuctionAuctions({
				...params,
				limit,
				page: pageParam as number | undefined,
				paginationMode: 'simple'
			});
		},
		queryKey: UseRPAuctionsQueryKeyBuilder(params, limit),
		staleTime: minutesToMs(5)
	});
};

export type AuctionData = FetchAuctionAuctionByAuctionIdData['data'] & {
	auctionBidsSorted?: Array<FrontAuctionBidDTO>;
};

export const useRPAuctionDetailsQueries = (
	auctionId: string,
	options?: Omit<UseQueryOptions<FetchAuctionAuctionByAuctionIdData, unknown, AuctionData>, 'queryFn'>
) => {
	return useQuery<FetchAuctionAuctionByAuctionIdData, unknown, AuctionData>({
		enabled: !!auctionId,
		queryFn: async () => (await rpAuctions).fetchAuctionAuctionByAuctionId(auctionId),
		queryKey: UseRPAuctionDetailsQueryKeyBuilder(auctionId),
		select: res => res?.data || {},
		staleTime: secondsToMs(30),
		...options
	});
};

export const useRPAuctionsMeQueries = (
	params?: FetchAuctionAuctionsMeParams,
	options?: Omit<
		UseInfiniteQueryOptions<FetchAuctionAuctionsMeData, Error, InfiniteData<FetchAuctionAuctionsMeData>>,
		'queryFn' | 'queryKey'
	>
) => {
	return useInfiniteQuery({
		...options,
		getNextPageParam: lastPage => {
			const metadata = lastPage.metadata as PaginationWithSimplePaginationMode['metadata'];

			const { current_page = 1, has_more_page } = metadata ?? {};

			return has_more_page ? current_page + 1 : undefined;
		},
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			return (await rpAuctions).fetchAuctionAuctionsMe({
				...params,
				page: pageParam as number | undefined,
				paginationMode: 'simple'
				// limit: 10 -> right now we are not implementing pagination for user auctions, add limit if we create the pagination system in the UI
			});
		},
		queryKey: [...UseRPAuctionsMeQueryKeyBuilder, params],
		staleTime: secondsToMs(5)
	});
};

export const useRPPostBidQueries = () => {
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation<
		PostAuctionAuctionBidByAuctionIdData,
		AxiosError<ApiErrorResponse>,
		PostRPBidParams,
		unknown
	>({
		mutationFn: async ({ amount, auctionId, countryCode }: PostRPBidParams) =>
			(await rpAuctions).postAuctionAuctionBidByAuctionId(
				auctionId,
				{
					amount,
					countryCode
				},
				{ customErrorInterceptor: () => true }
			),
		mutationKey: UsePlaceRPBidQueryKey
	});

	return {
		data,
		error,
		isPending,
		isSuccess,
		mutateAsync
	};
};
